/**
 * Chef-side bulk packing (PROD-1500): what a row says about the containers its
 * bulk deliverable rides in, from the plans `shipmentsToCampuses` attached
 * (`Deliverable.packing`).
 *
 * The plans are the shared contract (`@ordo/constants/packing-plan`): the
 * server works out each bulk package line's spec from the catalog sizes (its
 * container's capacity, each food's portion volume),
 * the servings of every line packed the same way on one campus x delivery day
 * x mealtime x order group are summed, and `planBulkPacking` runs ONCE (D54:
 * every container full, and the servings left over, "+ 5 EA", on an items
 * remaining sticker). Nothing here ever plans again: combining plans
 * (several delivery days on one weekly row, several schools on the
 * cross-school production row) only concatenates containers, through
 * `mergeBulkPackingPlans`.
 *
 * No spec, no plan: the row keeps today's servings display, with no warning
 * (the catalog team sees what to size in the Menus template builder).
 *
 * Pure, no React. Unit-tested in `test/lib/chefs/packing.test.ts`.
 */

import {
  type BulkPackingContainer,
  type BulkPackingPlan,
  bulkPackingSpecKey,
  containerNameFor,
  type ExtraMeasure,
  extraServingsParts,
  formatContainerCount,
  formatExtraServings,
  formatServingsPerContainer,
  mergeBulkPackingPlans,
} from '@ordo/constants/packing-plan'

import { pluralizeNoun } from './plural'
import type { Deliverable } from './types'

// ---------------------------------------------------------------------------
// Container words
// ---------------------------------------------------------------------------

/**
 * "Standard Hotel Pan" → "pan", "Bulk Delivery Box" → "box", "Paper Food Cup (8 oz)" → "cup": the last word of the
 * name up to its first comma or opening parenthesis, as `pluralizeContainerName` reads it, so a size after the noun
 * never becomes the word ('Full Hotel Pan, 2.5"' → "pan", the name staged for the Bulk Tray on Oct 20).
 */
export function containerShortName(container: Pick<BulkPackingContainer, 'name'>): string {
  const name = container.name.trim()
  const cut = name.search(/\s*[,(]/)
  const words = (cut === -1 ? name : name.slice(0, cut)).split(/\s+/).filter(Boolean)
  return (words[words.length - 1] ?? name).toLowerCase()
}

/** "17 pans", "1 pan": the row's count line under the servings. */
export function formatContainerCountShort(count: number, container: Pick<BulkPackingContainer, 'name'>): string {
  return `${count} ${pluralizeNoun(containerShortName(container), count)}`
}

/** "Pan 1": a container's label in the detail panel. */
export function containerOrdinalLabel(container: Pick<BulkPackingContainer, 'name'>, index: number): string {
  const short = containerShortName(container)
  return `${short.charAt(0).toUpperCase()}${short.slice(1)} ${index}`
}

function roundServings(servings: number): number {
  return Math.round(servings * 100) / 100
}

function servingsWords(servings: number): string {
  const n = roundServings(servings)
  return `${n} ${n === 1 ? 'serving' : 'servings'}`
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** Every container of `plans`, counted per container SKU in first-seen order; SKUs with none left out. */
function containerCounts(
  plans: ReadonlyArray<BulkPackingPlan>,
): Array<{ container: BulkPackingContainer; count: number }> {
  const counts = new Map<string, { container: BulkPackingContainer; count: number }>()
  for (const plan of plans) {
    const entry = counts.get(plan.container.skuId) ?? { container: plan.container, count: 0 }
    entry.count += plan.containerCount
    counts.set(plan.container.skuId, entry)
  }
  return [...counts.values()].filter((entry) => entry.count > 0)
}

/** The servings `plans` leave over once their containers are full (D54). */
function extraServings(plans: ReadonlyArray<BulkPackingPlan>): number {
  return plans.reduce((sum, plan) => sum + plan.extra, 0)
}

/** The unit every plan with leftovers counts them in, or servings when they differ ("+ 5 EA"). */
function sharedExtraMeasure(plans: ReadonlyArray<BulkPackingPlan>): ExtraMeasure | undefined {
  const measures = plans.filter((plan) => plan.extra > 0).map((plan) => JSON.stringify(plan.extraMeasure ?? null))
  if (new Set(measures).size > 1) {
    return { kind: 'SERVINGS' }
  }
  return plans.find((plan) => plan.extra > 0)?.extraMeasure
}

/**
 * A count with the servings `plans` leave over once their containers are full, said once at the end, in the food's
 * own unit: "+ 2.5 C", "+ 3 hamburgers", "+ 5 EA" (D54; never rounded up to another container).
 */
function withExtra(count: string, plans: ReadonlyArray<BulkPackingPlan>): string {
  const extra = formatExtraServings(extraServings(plans), sharedExtraMeasure(plans))
  return extra ? `${count} ${extra}` : count
}

/** One part of a short count, its number apart from its word: "16" "pans", "+ 5" "EA". */
export interface PackingCountPart {
  count: string
  word: string
}

/**
 * The row's count in parts: every container of its plans, counted per
 * container SKU ("16" "pans", "1" "box"), then the servings left over ("+ 5"
 * "EA"). When two containers would read the same short word ("Standard Hotel
 * Pan" and "Half Hotel Pan"), every count says its container's full name
 * instead. Empty when the plans hold no container.
 */
export function packingCountParts(plans: ReadonlyArray<BulkPackingPlan>): {
  containers: PackingCountPart[]
  extra: PackingCountPart | null
} {
  const entries = containerCounts(plans)
  const shortNames = entries.map((entry) => containerShortName(entry.container))
  const clash = new Set(shortNames).size < shortNames.length
  const containers = entries.map((entry, i) => ({
    count: String(entry.count),
    word: clash ? containerNameFor(entry.count, entry.container.name) : pluralizeNoun(shortNames[i]!, entry.count),
  }))
  const extra = extraServingsParts(extraServings(plans), sharedExtraMeasure(plans))
  return {
    containers,
    extra: containers.length > 0 && extra ? { count: `+ ${extra.count}`, word: extra.word } : null,
  }
}

/**
 * The row's count line: every container of its plans, counted per container
 * SKU, "16 pans", then the servings left over, "16 pans + 5 EA". Plans in two
 * kinds of container read "3 pans + 1 box"; when two containers would read the
 * same short word ("Standard Hotel Pan" and "Half Hotel Pan"), every count says
 * its container's full name instead: "3 Standard Hotel Pans + 2 Half Hotel
 * Pans". Null when the plans hold no container.
 */
export function packingCountLine(plans: ReadonlyArray<BulkPackingPlan>): string | null {
  const { containers, extra } = packingCountParts(plans)
  if (containers.length === 0) {
    return null
  }
  const counts = containers.map((part) => `${part.count} ${part.word}`).join(' + ')
  return extra ? `${counts} ${extra.count} ${extra.word}` : counts
}

/**
 * The containers counted with their full names, for paper: "17 Standard Hotel
 * Pans", "3 Standard Hotel Pans + 2 Half Hotel Pans", "16 Standard Hotel Pans
 * + 5 EA". Empty for no container.
 */
export function packingCountLabel(plans: ReadonlyArray<BulkPackingPlan>): string {
  const counts = containerCounts(plans)
    .map((entry) => formatContainerCount(entry.count, entry.container.name))
    .join(' + ')
  return counts ? withExtra(counts, plans) : ''
}

/**
 * The row's package description: "16x Standard Hotel Pans w/ 20 + 5 EA". A row
 * packed two ways (rare: a Section in its own container) lists the second
 * plan's runs after the first.
 */
export function packingDescription(plans: ReadonlyArray<BulkPackingPlan>): string {
  return plans
    .map((plan) => plan.description)
    .filter((description) => description.length > 0)
    .join(', ')
}

/** What the chef row shows for a row with packing (Figma 156:850). */
export interface PackingRowWords {
  /** Second line of the count column: "17 pans". Null when the plans hold no container. */
  countLine: string | null
  /** Subtitle line 1: the package description. */
  title: string
  /** Subtitle line 2: "1 Taco per serving"; null when the row has no serving. */
  detail: string | null
}

/** The row words for `plans`, with `servingSize` the row's recipe serving ("1 Taco"). */
export function packingRowWords(
  plans: ReadonlyArray<BulkPackingPlan>,
  servingSize: string | undefined,
): PackingRowWords {
  return {
    countLine: packingCountLine(plans),
    title: packingDescription(plans),
    detail: servingSize ? `${servingSize} per serving` : null,
  }
}

/** A row's packing state, as a cross-school merge reads and writes it. */
export type RowPacking = Pick<Deliverable, 'packing' | 'packedWith' | 'packingIncomplete'>

/**
 * Two rows' packing merged into one (the cross-school production row, which
 * keeps the name `rowName`). When either row carries servings no spec covers
 * (`packingIncomplete`), the merged row shows no containers and no "packed
 * with" at all: its quantity holds servings no pan covers. Otherwise the plans
 * pool per way of packing (`mergeBulkPackingPlans`: containers concatenated
 * fullest first, never a re-plan, since a pan never travels between schools),
 * a row counted in another row's containers adds its "packed with" names, and
 * the merged row never points at itself. A side's pans never disappear while
 * another side's stay.
 */
export function mergeDeliverablePacking(a: RowPacking, b: RowPacking, rowName: string): RowPacking {
  if (a.packingIncomplete || b.packingIncomplete) {
    return { packingIncomplete: true }
  }
  const packing = mergeBulkPackingPlans([...(a.packing ?? []), ...(b.packing ?? [])])
  const packedWith = [...new Set([...(a.packedWith ?? []), ...(b.packedWith ?? [])])].filter((name) => name !== rowName)
  return {
    ...(packing.length > 0 ? { packing } : {}),
    ...(packedWith.length > 0 ? { packedWith } : {}),
  }
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

/** One container line in the detail panel: "Pan 1" | "20 servings", or "+ 5 EA" | "Items remaining: …". */
export interface PackedContainerWords {
  label: string
  content: string
}

/** One plan as the detail panel's Packing section lists it (Figma 156:965). */
export interface PackingPanelPlan {
  key: string
  /** "98 servings per Standard Hotel Pan". */
  heading: string
  /** One line per container, fullest first, numbered from 1, then the servings left over (D54). */
  containers: PackedContainerWords[]
}

/** The leftover servings' line: Matt's "items remaining" sticker, and where it goes (Oct 20). */
export const ITEMS_REMAINING_NOTE = 'Items remaining: add them to a pan with room, or use another pan'

/**
 * The detail panel's Packing section: per plan, how many servings fit in one
 * container (`formatServingsPerContainer`), then one line per container ("Pan
 * 1 · 20 servings"), numbered within the plan, and the servings left over
 * ("+ 5 EA · Items remaining: …").
 */
export function packingPanelPlans(plans: ReadonlyArray<BulkPackingPlan>): PackingPanelPlan[] {
  return plans.map((plan) => ({
    key: bulkPackingSpecKey(plan),
    heading: formatServingsPerContainer(plan.servingsPerContainer, plan.container.name),
    containers: [
      ...plan.containers.map((servings, i) => ({
        label: containerOrdinalLabel(plan.container, i + 1),
        content: servingsWords(servings),
      })),
      ...(plan.extra > 0
        ? [{ label: formatExtraServings(plan.extra, plan.extraMeasure), content: ITEMS_REMAINING_NOTE }]
        : []),
    ],
  }))
}
